/**
 * Os guias das três conexões que o cliente faz sozinho.
 *
 * ### Por que isto existe, e por que mora AQUI e não só no curso
 *
 * O painel já tinha um passo a passo na tela do Meta e ainda assim o próprio dono tropeçou
 * três vezes seguidas ao conectar: marcou a permissão errada, escreveu o nome da variável em
 * minúsculas e quase deixou a conta sem ser atribuída ao Usuário do Sistema. O guia antigo
 * explicava **o caminho** e não **as pedras**.
 *
 * Um curso noutra aba não salva ninguém: a pessoa erra no momento em que está com o formulário
 * aberto, e é ali que a resposta precisa estar. O curso dá o panorama e aponta para cá.
 *
 * ### A parte que muda o resultado: `quandoNaoFunciona`
 *
 * Todo passo a passo do mundo descreve o caminho feliz. O que trava gente de verdade é o
 * estado em que **tudo parece certo e nada acontece** — e nesta integração isso é a regra, não
 * a exceção: um nome de variável com uma letra diferente faz o sincronizador pular a conexão
 * em SILÊNCIO, sem erro em tela nenhuma. Por isso cada guia carrega uma lista de
 * sintoma → causa, escrita a partir dos erros que aconteceram de verdade, não imaginados.
 */

export interface PassoDeGuia {
  titulo: string;
  texto: string;
  /** Valor literal para copiar (nome de variável, trecho de código). Aparece em monoespaçada. */
  exemplo?: string;
  /** O detalhe que faz o passo "parecer feito" sem estar. Aparece destacado. */
  atencao?: string;
}

export interface SintomaEcausa {
  /** O que a pessoa vê. Escrito com as palavras dela, não com as nossas. */
  sintoma: string;
  causa: string;
}

export interface GuiaDeConexao {
  id: string;
  titulo: string;
  /** Uma frase: o que essa conexão faz o produto enxergar. */
  resumo: string;
  passos: readonly PassoDeGuia[];
  quandoNaoFunciona: readonly SintomaEcausa[];
}

/* ========================================================================== */
/*  Meta Ads                                                                   */
/* ========================================================================== */

export const GUIA_META_ADS: GuiaDeConexao = {
  id: 'meta-ads',
  titulo: 'Conectar a conta de anúncios da Meta',
  resumo:
    'É o que permite cruzar o quanto você gastou no anúncio com quantas pessoas entraram no grupo — o custo por entrada real, que o Gerenciador de Anúncios não mostra.',
  passos: [
    {
      titulo: 'Tenha a conta de anúncios dentro do Gerenciador de Negócios',
      texto:
        'É o Business Manager, em business.facebook.com. Conta de anúncios que vive solta no perfil pessoal de alguém não pode ser lida por sistema nenhum — precisa pertencer a um negócio.',
    },
    {
      titulo: 'Crie um Usuário do Sistema',
      texto:
        'No Gerenciador: Configurações do negócio › Usuários › Usuários do sistema › Adicionar. É um "funcionário robô", e existe por um motivo prático: o token dele não vence sozinho. Token de pessoa vence em cerca de 60 dias e morre junto quando ela sai da empresa — e aí os números param sem ninguém entender por quê.',
    },
    {
      titulo: 'Dê a ele acesso à conta de anúncios',
      texto:
        'Ainda no Usuário do Sistema: Adicionar ativos › Contas de anúncios › marque a sua conta › permissão "Ver desempenho". Só isso. Não precisa de "Gerenciar campanhas".',
      atencao:
        'Este é o passo mais esquecido, e o que falha pior: com ele faltando, o token é gerado normalmente e a Meta responde erro de permissão na hora de ler — sem dizer que o problema é a conta não estar atribuída. Permissão do token e acesso à conta são duas coisas separadas.',
    },
    {
      titulo: 'Gere o token marcando SÓ ads_read',
      texto:
        'No mesmo Usuário do Sistema: Gerar token › escolha o app › expiração "Nunca expira" › e nas permissões marque apenas ads_read.',
      atencao:
        'ads_management é opcional: só marque se quiser ajustar o orçamento das campanhas pelo MGDLead (botão "Ajustar"). Sem ela, o painel só lê os números. business_management não é usada: o ID da conta você informa aqui.',
    },
    {
      titulo: 'Cole o token aqui no painel',
      texto:
        'Em Meta Ads › Conectar conta de anúncios, informe o número da conta e cole o token. Antes de salvar, o MGDLead pergunta à Meta se o token lê aquela conta — se algo estiver errado, você descobre na hora, com o formulário aberto. O token fica guardado cifrado e não aparece mais na tela: só o final dele, para você saber qual é.',
      atencao:
        'Copie o token INTEIRO (ele começa com EAA e é longo). Se ele vencer ou for revogado, use "Trocar token" no cartão da conta — não precisa desconectar.',
    },
    {
      titulo: 'Se a conta for de um cliente, peça Acesso Avançado a ads_read',
      texto:
        'É o App Review da Meta, com verificação do negócio, e leva de alguns dias a algumas semanas. Para ler a SUA própria conta, o acesso padrão já basta — este passo só existe quando o app precisa enxergar conta de terceiro.',
    },
  ],
  quandoNaoFunciona: [
    {
      sintoma: 'Conectei e os números não aparecem.',
      causa:
        'A leitura acontece a cada 10 minutos: a primeira rodada pode levar alguns minutos depois de conectar. Se passou disso e o cartão mostra erro, use "Colar um token novo".',
    },
    {
      sintoma: 'Ao conectar, a tela diz que a Meta recusou o token.',
      causa:
        'A conta de anúncios não foi atribuída ao Usuário do Sistema (o terceiro passo). O token tem a permissão; o que falta é o acesso ao ativo.',
    },
    {
      sintoma: 'Conectou certo e os números só aparecem depois.',
      causa:
        'É o esperado: a leitura acontece a cada 10 minutos. Se não quiser esperar, toque no ↻ ao lado de "Meta" no topo do Dashboard.',
    },
    {
      sintoma: 'Funcionava e parou, com aviso de token expirado.',
      causa:
        'Token de Usuário do Sistema não vence sozinho, mas é revogável: alguém pode ter apagado o usuário, retirado a permissão ou removido a conta do negócio. Gere outro e use "Trocar token" no cartão da conta — o histórico continua.',
    },
  ],
};

/* ========================================================================== */
/*  WhatsApp                                                                   */
/* ========================================================================== */

export const GUIA_WHATSAPP: GuiaDeConexao = {
  id: 'whatsapp',
  titulo: 'Conectar o número de WhatsApp',
  resumo:
    'É o que faz o produto enxergar quem entra e quem sai dos seus grupos — a permanência, que é o número mais sólido daqui e que nenhum gerenciador de anúncio mostra.',
  passos: [
    {
      titulo: 'Escolha um número que vai ficar no ar',
      texto:
        'O aparelho precisa continuar ligado e com internet. Não use o número pessoal que você desconecta à noite: cada queda é um pedaço de história que não volta, porque entrada e saída só chegam enquanto o número está conectado.',
    },
    {
      titulo: 'Crie o monitorador e leia o QR',
      texto:
        'Dê um nome que diga qual número é, aponte a câmera do WhatsApp para o QR e espere o estado virar "Conectado". O QR expira em poucos minutos — se demorar, peça outro.',
    },
    {
      titulo: 'Coloque esse número dentro dos grupos',
      texto:
        'O produto só enxerga grupo em que o número monitorador está dentro. Não precisa ser administrador para ver entradas e saídas.',
    },
    {
      titulo: 'Importe os grupos e faça a primeira varredura',
      texto:
        'A varredura é o que transforma "sei quantos são" em "sei quem são" — e sem ela a exportação de contatos sai vazia.',
      atencao:
        'A primeira varredura de propósito NÃO conta como entrada de ninguém. Achar 900 pessoas num grupo recém-importado não é ter recebido 900 entradas hoje; marcar assim inventaria um pico que ficaria na história para sempre.',
    },
  ],
  quandoNaoFunciona: [
    {
      sintoma: 'O número caiu e eu reconectei, mas faltou gente na conta.',
      causa:
        'Enquanto esteve fora, as entradas e saídas daquele período não chegaram — elas não são recuperáveis depois. A varredura corrige QUEM está no grupo agora, mas não reconstrói quando cada um entrou.',
    },
    {
      sintoma: 'Desconectei pelo celular, em "Aparelhos conectados".',
      causa:
        'Isso derruba a sessão sem o painel saber. Para tirar um monitorador do ar, use o botão da tela — apagar por fora deixa a sessão viva do outro lado, tentando entregar eventos que ninguém mais recebe.',
    },
    {
      sintoma: 'A permanência está vazia para quem já estava no grupo.',
      causa:
        'É o esperado, e não é defeito: não temos como saber quando alguém entrou antes de o monitoramento existir. Permanência e retenção passam a existir para quem entra a partir de agora. Carimbar a data de hoje para todo mundo deixaria o número bonito e falso.',
    },
  ],
};

/* ========================================================================== */
/*  Redirecionador na landing page                                             */
/* ========================================================================== */

export const GUIA_REDIRECIONADOR: GuiaDeConexao = {
  id: 'redirecionador',
  titulo: 'Instalar o link na sua página',
  resumo:
    'É o elo que liga o anúncio ao grupo. Sem ele, o painel mostra entradas e nunca consegue dizer de qual campanha elas vieram.',
  passos: [
    {
      titulo: 'Crie o redirecionador e vincule os grupos',
      texto:
        'Ele decide para qual grupo cada pessoa vai. Grupo sem convite buscado não é elegível — o painel busca o convite por você, mas alguém precisa pedir.',
    },
    {
      titulo: 'Copie o trecho pronto e cole no botão da sua página',
      texto:
        'A tela entrega o trecho montado. Ele é o endereço do botão "Entrar no grupo" da sua landing page.',
      atencao:
        'Cole o trecho INTEIRO. É aqui que quase todo mundo erra: copiar só o endereço e deixar o botão com um link fixo faz o clique chegar sem nenhuma marca da campanha — o painel registra a visita, mas não tem como dizer de onde ela veio, e a atribuição morre sem nenhum erro aparecer.',
    },
    {
      titulo: 'Clique você mesmo, uma vez, antes de ligar o anúncio',
      texto:
        'Abra a sua página, clique no botão e veja se você cai no grupo. Depois confira no painel se o clique apareceu com a origem certa. Um minuto agora vale mais que qualquer teste automático — é o único jeito de saber que o caminho inteiro funciona com dinheiro real.',
    },
  ],
  quandoNaoFunciona: [
    {
      sintoma: 'Os cliques aparecem, mas todos como "sem origem".',
      causa:
        'O botão da página está com um link fixo em vez do trecho completo. O clique chega, a pessoa entra no grupo, e mesmo assim não dá para dizer qual anúncio pagou por ela. Se houver verba rodando, isso é dinheiro sem medição.',
    },
    {
      sintoma: 'O clique não leva a grupo nenhum.',
      causa:
        'Nenhum grupo elegível: ou não há grupo vinculado, ou nenhum deles tem convite buscado, ou todos estão cheios. O painel abre um alerta para isso — e ele é urgente quando existe campanha ativa, porque é tráfego pago caindo no vazio.',
    },
    {
      sintoma: 'Troquei o link do convite no WhatsApp e parou de funcionar.',
      causa:
        'Redefinir o convite pelo próprio WhatsApp invalida o que o painel guardava. Peça para buscar o convite de novo na tela do grupo.',
    },
  ],
};

export const GUIAS: readonly GuiaDeConexao[] = [GUIA_META_ADS, GUIA_WHATSAPP, GUIA_REDIRECIONADOR];
